/**
 * @file Purchase Ppd Cart Item Toggle HOC component.
 * Attempts to fully handle selecting/deselecting items from
 * the cart based on the provided context.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash.get';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';

import { sentryCaptureMessage } from '../../util/sentry';
import {
  canPurchaseDownloadsAndPrints,
  isCartItemSelected,
  hasSubscriptionInCart,
  hasDownloadInCart,
} from '../../util/ppd-purchase';
import { PPD_CART_ITEM_TYPES } from '../../constants/ppd-purchase';
import {
  selectDownload as selectDownloadAction,
  deselectDownload as deselectDownloadAction,
  selectPrint as selectPrintAction,
  deselectPrint as deselectPrintAction,
  selectPrintPackage as selectPrintPackageAction,
  deselectPrintPackage as deselectPrintPackageAction,
  selectSubscription as selectSubscriptionAction,
  deselectSubscription as deselectSubscriptionAction,
  selectAlbumCustomPrint,
  deselectAlbumCustomPrint,
} from '../../actions/ppd-purchase';
import { getMergedMatchedPhotosForPhotoPricingPurchase } from '../../util/purchase-album-photos';
import {
  MeForAlbumPurchase,
  MeForPpdPurchasePerIdentity,
} from '../../graphql/queries/account';
import {
  AlbumForPpdPurchase,
  AlbumMembershipWithPhotoTagPrice,
} from '../../graphql/queries/albums';
import { getAlbumMembership } from '../../util/account';
import Spinner from '../../components/global/Spinner';
import Alert from '../../components/global/Alert';

const withPurchasePpdCartItemToggleHOC = WrappedComponent =>
  class PurchasePpdCartItemToggle extends Component {
    static propTypes = {
      user: PropTypes.shape({
        id: PropTypes.string,
        purchased_print_packages: PropTypes.shape({
          total: PropTypes.number,
        }),
      }),
      selectedIdentities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
      album: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        default_price_per_photo: PropTypes.number,
        is_demo: PropTypes.bool.isRequired,
        album_folder: PropTypes.shape({
          name: PropTypes.string,
        }),
        subscription_album_folder: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          price: PropTypes.shape({
            value: PropTypes.number,
          }),
        }),
      }),
      cart: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(Object.keys(PPD_CART_ITEM_TYPES)),
          photo: PropTypes.shape({
            id: PropTypes.string,
          }),
          printSize: PropTypes.shape({
            id: PropTypes.string,
          }),
          printPackage: PropTypes.shape({
            id: PropTypes.string,
          }),
          quantity: PropTypes.number,
        }),
      ),
      selectDownload: PropTypes.func.isRequired,
      deselectDownload: PropTypes.func.isRequired,
      selectPrint: PropTypes.func.isRequired,
      deselectPrint: PropTypes.func.isRequired,
      selectPrintPackage: PropTypes.func.isRequired,
      deselectPrintPackage: PropTypes.func.isRequired,
      matchedPhotos: PropTypes.arrayOf(
        PropTypes.shape({
          comparedFaces: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
          photo: PropTypes.shape({
            id: PropTypes.string,
            thumbnail_urls: PropTypes.arrayOf(
              PropTypes.shape({
                width: PropTypes.number,
                height: PropTypes.number,
                url: PropTypes.string,
              }),
            ),
          }),
        }),
      ),
      loadingMembership: PropTypes.bool,
      albumMembership: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    };

    componentDidUpdate(prevProps) {
      const {
        cart,
        album,
        user,
        selectedIdentities,
        selectPrintPackage,
        albumMembership,
        loadingMembership,
      } = this.props;

      if (
        // Check for undefined to avoid the
        // message from triggering before the query
        // even has a chance to run.
        typeof loadingMembership !== 'undefined' &&
        !loadingMembership &&
        !albumMembership
      ) {
        sentryCaptureMessage(
          '[PPD Purchase - withPurchasePpdCartItemToggleHOC] - No membership found...',
          {
            level: 'error',
            extra: {
              user: get(user, 'id'),
              album: get(album, 'id'),
            },
          },
        );
      }

      if (!album) {
        return;
      }

      if (prevProps.cart.length !== cart.length) {
        // Clear all items if downloadsAndPrints gets disabled at some point for a
        // specific identity
        selectedIdentities.forEach(selectedIdentity => {
          const downloadsPrintsEnabled = canPurchaseDownloadsAndPrints(
            cart,
            album,
            selectedIdentity,
            user,
          );

          const hasSubscriptionSelected = hasSubscriptionInCart(
            cart,
            selectedIdentity.id,
          );

          const hasDownloadsSelected = hasDownloadInCart(
            cart,
            selectedIdentity.id,
          );

          if (!downloadsPrintsEnabled) {
            // Deselect all items:
            cart.forEach(cartItem => {
              if (
                get(cartItem, 'selectedIdentity.id') ===
                  get(selectedIdentity, 'id') &&
                cartItem.type !== PPD_CART_ITEM_TYPES.SUBSCRIPTION &&
                cartItem.type !== PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT
              ) {
                this.toggleCartItem(
                  cartItem.photo.id,
                  cartItem.type,
                  cartItem.printSize,
                );
              }
            });
          }

          if (hasSubscriptionSelected && hasDownloadsSelected) {
            // Deselect all Download:
            cart.forEach(cartItem => {
              if (
                get(cartItem, 'selectedIdentity.id') !==
                get(selectedIdentity, 'id')
              ) {
                return;
              }

              if (cartItem.type === PPD_CART_ITEM_TYPES.DOWNLOAD) {
                this.toggleCartItem(
                  cartItem.photo.id,
                  cartItem.type,
                  cartItem.printSize,
                );
              }

              // Deselect print package download upsells.
              if (
                cartItem.type === PPD_CART_ITEM_TYPES.PRINT_PACKAGE &&
                cartItem.digitalDownloadUpsell
              ) {
                selectPrintPackage(
                  cartItem.photo,
                  cartItem.printPackage,
                  cartItem.quantity,
                  false,
                );
              }
            });
          }
        });
      }
    }

    toggleCartItem = (photoId, selectionType, sizeOrPackage, quantity) => {
      const {
        cart,
        matchedPhotos,
        selectDownload,
        deselectDownload,
        selectPrint,
        deselectPrint,
        selectPrintPackage,
        deselectPrintPackage,
      } = this.props;

      const { photo } = matchedPhotos.find(
        photoItem => get(photoItem, 'photo.id') === photoId,
      );

      if (!photo) {
        return;
      }

      const isCartItemSelectedBool = isCartItemSelected(
        cart,
        photoId,
        selectionType,
        sizeOrPackage,
      );

      if (!isCartItemSelectedBool) {
        switch (selectionType) {
          case PPD_CART_ITEM_TYPES.DOWNLOAD: {
            selectDownload(photo);
            break;
          }
          case PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT: {
            selectPrint(photo, sizeOrPackage, quantity);
            break;
          }
          case PPD_CART_ITEM_TYPES.PRINT_PACKAGE: {
            selectPrintPackage(photo, sizeOrPackage, quantity);
            break;
          }
          default: {
            break;
          }
        }
      } else {
        switch (selectionType) {
          case PPD_CART_ITEM_TYPES.DOWNLOAD: {
            deselectDownload(photo);
            break;
          }
          case PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT: {
            deselectPrint(photo, sizeOrPackage);
            break;
          }
          case PPD_CART_ITEM_TYPES.PRINT_PACKAGE: {
            deselectPrintPackage(photo, sizeOrPackage);
            break;
          }
          default: {
            break;
          }
        }
      }
    };

    render() {
      const {
        cart,
        album,
        user,
        selectedIdentities,
        loadingMembership,
        albumMembership,
        ...props
      } = this.props;

      if (loadingMembership) {
        return (
          <div className="flex-auto text-center mt-5">
            <Spinner />
          </div>
        );
      }

      if (!albumMembership) {
        return (
          <Alert type="danger" className="text-center m-5">
            There does not seem to be a membership to this album. Please{' '}
            <a
              href="mailto:help@waldophotos.com"
              className="text-white text-bold"
            >
              contact
            </a>{' '}
            the Waldo team for more help if you are certain you have subscribed
            to this album.
          </Alert>
        );
      }

      return (
        <WrappedComponent toggleCartItem={this.toggleCartItem} {...props} />
      );
    }
  };

const WithPurchasePpdCartItemToggle = WrappedComponent => {
  const withPurchasePpdCartItemToggle = withPurchasePpdCartItemToggleHOC(
    WrappedComponent,
  );

  const withAlbumMembershipWithPhotoTagPrice = graphql(
    AlbumMembershipWithPhotoTagPrice,
    {
      options: ({ albumMembership, match }) => ({
        variables: {
          album_id: match.params.albumId,
          id: get(albumMembership, 'id'),
        },
      }),
      // It's possible that we get into loading this query before
      // we actually have the album membership. If we do, we'll
      // skip it. Album membership will end up here later on regardless.
      skip: ({ albumMembership }) => !get(albumMembership, 'id'),
      props: ({ data: { loading, albumMembership } }) => {
        return {
          matchedPhotos: getMergedMatchedPhotosForPhotoPricingPurchase(
            albumMembership,
          ),
          entitledPhotosNo: get(
            albumMembership,
            'entitled_matched_photos.total',
          ),
          entitledPhotos: get(
            albumMembership,
            'entitled_matched_photos.records',
            [],
          ),
          loading,
        };
      },
    },
  );

  const withAlbumForPpdPurchase = graphql(AlbumForPpdPurchase, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      album,
      loading,
    }),
  });

  const withMeForAlbumPurchase = graphql(MeForAlbumPurchase, {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
      },
    }),
    props: ({ data: { me, loading }, ownProps: { match } }) => ({
      user: me,
      albumMembership: getAlbumMembership(me, match.params.albumId),
      loadingMembership: loading,
    }),
  });

  const withMatchedPhotosFromUser = graphql(MeForPpdPurchasePerIdentity, {
    options: ({ match }) => ({
      variables: {
        albumId: match.params.albumId,
      },
    }),
    props: ({ ownProps, data: { me, loading } }) => {
      const albumMembership = getAlbumMembership(
        me,
        ownProps.match.params.albumId,
      );

      if (!albumMembership) {
        return {
          loading,
        };
      }

      const selectedIdentities = get(
        albumMembership,
        'selected_identities.records',
        [],
      );
      return {
        loading,
        selectedIdentities,
      };
    },
  });

  const mapStateToProps = ({ ppdPurchase }) => ({
    cart: ppdPurchase.cart,
    activeIdentity: ppdPurchase.selectedIdentity,
  });

  const mapDispatchToProps = dispatch => ({
    selectDownload: photo => dispatch(selectDownloadAction(photo)),
    deselectDownload: photo => dispatch(deselectDownloadAction(photo)),
    selectPrint: (photo, printSize, quantity) =>
      dispatch(selectPrintAction(photo, printSize, quantity)),
    deselectPrint: (photo, printSize) =>
      dispatch(deselectPrintAction(photo, printSize)),
    selectPrintPackage: (photo, printPackage, quantity) =>
      dispatch(selectPrintPackageAction(photo, printPackage, quantity)),
    deselectPrintPackage: (photo, printPackage) =>
      dispatch(deselectPrintPackageAction(photo, printPackage)),
    selectSubscription: identity =>
      dispatch(selectSubscriptionAction(identity)),
    deselectSubscription: identity =>
      dispatch(deselectSubscriptionAction(identity)),
    selectAlbumCustomPrint: (
      selectedIdentity,
      albumCustomPrint,
      includeShipping,
    ) =>
      dispatch(
        selectAlbumCustomPrint(
          selectedIdentity,
          albumCustomPrint,
          includeShipping,
        ),
      ),
    deselectAlbumCustomPrint: (selectedIdentity, albumCustomPrint) =>
      dispatch(deselectAlbumCustomPrint(selectedIdentity, albumCustomPrint)),
  });

  return compose(
    withRouter,
    withMeForAlbumPurchase,
    withAlbumForPpdPurchase,
    connect(mapStateToProps, mapDispatchToProps),
    withMatchedPhotosFromUser,
    withAlbumMembershipWithPhotoTagPrice,
  )(withPurchasePpdCartItemToggle);
};

export { withPurchasePpdCartItemToggleHOC };
export default WithPurchasePpdCartItemToggle;
